import {
  closestCenter,
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  horizontalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import {
  DataTableDragCell,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHeader,
  TableRow,
} from '@ui/components';
import { cn } from '@ui/lib/utils';
import {
  ForwardedRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useAppTableContext } from '../../lib/context/use-app-table-context';
import { useDefaultTable } from '../../lib/hooks/use-default-table.tsx';
import { useSkeletonRowCount } from '../../lib/hooks/use-skeleton-row-count';
import { AppTableNewRef } from '../../types/table-props';
import {
  AppTableDragHeaderFilter,
  AppTableHeader,
} from '../app-table-header/index.tsx';
import { AppTablePagination } from '../app-table-pagination/index.tsx';
import { AppTableSkeleton } from '../app-table-skeleton';
import { AppTableToolbar } from '../app-table-toolbar';
import { TableCard } from '../table-card';

const TABLE_SCROLL_CONTAINER_CLASS =
  'min-h-0 pb-4 [scrollbar-gutter:stable_both-edges]';

const TableComponent = <TData,>(
  _: never,
  ref: ForwardedRef<AppTableNewRef<TData>>,
) => {
  const {
    className,
    data,
    columns,
    isHeaderSticky,
    isLoading,
    isSkeleton,
    isResizable,
    isDraggable,
    disablePagination,
    disableToolbar,
    enableColumnFilter,
    enableFooter,
    hiddenColumns,
    rowHeight,
    rowSelection,
    setRowSelection,
    getRowId,
    onClickRow,
    onDoubleClickRow,
    columnPinning,
    isTemplate,
    rowClassName,
    calculatedRowClassName,
    calculatedRowStyle,
    defaultSorting,
    manualPagination,
    onPaginationChange,
    onSearchChange,
    onSortingChange,
    pageSizeOptions,
    pagination,
    sorting,
    totalRows,
  } = useAppTableContext<TData>();

  const { table, tableColumns, columnOrder, handleDragEnd } = useDefaultTable({
    data,
    columns,
    isResizable,
    hiddenColumns,
    enableColumnFilter,
    disablePagination,
    rowSelection,
    setRowSelection,
    getRowId,
    columnPinning,
    isTemplate,
    defaultSorting,
    manualPagination,
    onPaginationChange,
    onSearchChange,
    onSortingChange,
    pagination,
    sorting,
    totalRows,
  });
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const tableHeaderRef = useRef<HTMLTableSectionElement>(null);
  const skeletonRowHeight = rowHeight ?? 38;
  const skeletonRowCount = useSkeletonRowCount({
    containerRef: tableContainerRef,
    headerRef: tableHeaderRef,
    rowHeight: skeletonRowHeight,
  });

  const columnSizeVars = useMemo(() => {
    const headers = table.getFlatHeaders();
    const colSizes: { [key: string]: number } = {};
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]!;
      colSizes[`--header-${header.id}-size`] = header.getSize();
      colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
    }
    return colSizes;
  }, [table.getState().columnSizingInfo, table.getState().columnSizing]);

  useImperativeHandle(ref, () => ({
    getTable: () => table,
  }));

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
  );

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      sensors={sensors}
      onDragEnd={handleDragEnd}
    >
      <TableCard className={cn('flex flex-col overflow-hidden', className)}>
        {!disableToolbar && <AppTableToolbar table={table} />}
        <Table
          className={cn('w-full', !isSkeleton && !data.length && 'h-full')}
          containerRef={tableContainerRef}
          containerClassName={TABLE_SCROLL_CONTAINER_CLASS}
          style={{
            width:
              table.getTotalSize() < document.body.offsetWidth
                ? undefined
                : table.getTotalSize(),
            ...columnSizeVars,
          }}
        >
          <TableHeader
            ref={tableHeaderRef}
            className={cn(
              disableToolbar && '[&_tr]:border-t-0',
              isHeaderSticky && 'sticky top-0 z-10 bg-white',
            )}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                <SortableContext
                  disabled={!isDraggable}
                  items={columnOrder}
                  strategy={horizontalListSortingStrategy}
                >
                  {headerGroup.headers.map((header) => (
                    <AppTableHeader
                      key={header.id}
                      header={header}
                    />
                  ))}
                </SortableContext>
              </TableRow>
            ))}
            {!!enableColumnFilter &&
              table.getHeaderGroups().map((headerGroup) => {
                return (
                  <TableRow
                    key={headerGroup.id}
                    className="hover:bg-inherit"
                  >
                    <SortableContext
                      disabled={!isDraggable}
                      items={columnOrder}
                      strategy={horizontalListSortingStrategy}
                    >
                      {headerGroup.headers.map((header) => (
                        <AppTableDragHeaderFilter
                          key={header.id}
                          header={header}
                        />
                      ))}
                    </SortableContext>
                  </TableRow>
                );
              })}
          </TableHeader>
          <TableBody className={cn('border-b')}>
            {isSkeleton ? (
              <AppTableSkeleton
                columns={table.getVisibleLeafColumns().map((column) => ({
                  id: column.id,
                  width: `calc(var(--col-${column.id}-size) * 1px)`,
                }))}
                rowCount={skeletonRowCount}
                rowHeight={skeletonRowHeight}
              />
            ) : table.getRowModel().rows?.length && !isLoading ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={cn(
                    rowClassName,
                    calculatedRowClassName?.(row.original, row),
                    onDoubleClickRow || onClickRow
                      ? 'cursor-pointer'
                      : 'cursor-default',
                  )}
                  data-state={row.getIsSelected() && 'selected'}
                  style={calculatedRowStyle?.(row.original, row)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <SortableContext
                      key={cell.id}
                      disabled={!isDraggable}
                      items={columnOrder}
                      strategy={horizontalListSortingStrategy}
                    >
                      <DataTableDragCell
                        key={cell.id}
                        cell={cell}
                        classNameCell="text-xs"
                        styleCell={{
                          width: `calc(var(--col-${cell.column.id}-size) * 1px)`,
                        }}
                        onDoubleClick={onDoubleClickRow}
                        onRowClick={onClickRow}
                      />
                    </SortableContext>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow className="size-full hover:bg-inherit">
                <TableCell
                  className={cn('h-24 text-center')}
                  colSpan={tableColumns.length}
                >
                  {isLoading ? <Spinner /> : 'No results.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {enableFooter && table.getFooterGroups().length > 0 && (
            <TableFooter>
              {table.getFooterGroups().map((footerGroup) => (
                <TableRow key={footerGroup.id}>
                  {footerGroup.headers.map((header) => (
                    <TableCell
                      key={header.id}
                      className="font-semibold"
                      style={{
                        width: `calc(var(--col-${header.column.id}-size) * 1px)`,
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : header.column.columnDef.footer &&
                          (typeof header.column.columnDef.footer === 'function'
                            ? header.column.columnDef.footer({
                                column: header.column,
                                header,
                                table,
                              })
                            : header.column.columnDef.footer)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableFooter>
          )}
        </Table>
        {!disablePagination && (
          <AppTablePagination
            pageSizeOptions={pageSizeOptions}
            table={table}
          />
        )}
      </TableCard>
    </DndContext>
  );
};

export const DefaultTable = forwardRef(TableComponent) as <TData>(
  props: never & {
    ref?: ForwardedRef<AppTableNewRef<TData>>;
  },
) => ReturnType<typeof TableComponent>;
